import { Fragment, useEffect, useRef, useState, type FC, type ReactNode } from 'react';
import { LuCheck, LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { useLocation } from 'react-router-dom';
import { useAgente } from '../contexts/AgenteContext';
import AgentePorta, { rotaSemPortaDoAgente } from './AgentePorta';
import useCelular from '../hooks/useCelular';
import './PageHeader.css';

export interface PageTab {
    id: string;
    label: string;
    icon?: ReactNode;
    /**
     * Número discreto depois do rótulo, quando a aba precisa dizer QUANTO tem
     * do outro lado — em Metas é quantas pessoas já têm meta naquela métrica,
     * que é o que denuncia a métrica esquecida sem obrigar a abrir a aba.
     * Omitir some com o número.
     */
    count?: number;
}

export interface PageStep {
    id: string;
    label: string;
    /** Número dentro do círculo. Só o rótulo muda entre telas; a ordem é a do array. */
    meta?: string;
    /** Passo já cumprido: ganha o tique e passa a ser clicável. */
    done?: boolean;
}

interface PageHeaderProps {
    /**
     * Slot à esquerda do título, antes dele. Existe para as telas de
     * detalhe (que abrem a partir de uma lista) poderem pôr o "voltar"
     * sem inventar um segundo cabeçalho.
     */
    leading?: ReactNode;
    /** Título da tela — 24px/600. */
    title: string;
    /**
     * Acessório logo depois do título, na mesma linha: um "i" de informação, um
     * selo de estado. Fica DENTRO do `h1` para acompanhar a linha de base do
     * texto — ao lado, num flex externo, ele flutuaria acima em títulos de duas
     * linhas. Nasceu em Relatórios, onde a explicação do cálculo saiu de um botão
     * do cabeçalho e virou tooltip no título.
     */
    titleInfo?: ReactNode;
    /** Linha de apoio opcional, uma frase curta dizendo para que serve a tela. */
    subtitle?: string;
    /** Filtros e ações da tela, alinhados à direita do título. */
    actions?: ReactNode;
    /** Abas da tela. Omitir some com a segunda faixa inteira. */
    tabs?: PageTab[];
    activeTab?: string;
    onTabChange?: (id: string) => void;
    /**
     * Passos de um fluxo, na MESMA faixa das abas (44px) e com a mesma
     * geometria de pill. A semântica é outra: aba é visão do mesmo conteúdo,
     * passo é progresso — por isso o círculo numerado, o tique no que já foi
     * cumprido e o filete ligando um ao outro.
     *
     * Só anda para trás ou para um passo já cumprido: o avanço é do botão
     * primário do cabeçalho, que é quem sabe se o passo atual está completo.
     * Nasceu no Disparador (2026-09-18), que é o único fluxo em passos do app.
     */
    steps?: PageStep[];
    activeStep?: string;
    onStepChange?: (id: string) => void;
    /**
     * Conteúdo livre na MESMA segunda faixa (44px, divisória de ponta a ponta)
     * que abas e passos ocupam, para a tela que precisa de outra coisa ali.
     * Nasceu na oportunidade (2026-09-23): a régua de etapas subiu para esta
     * faixa e as abas desceram para dentro do painel. Só a tela sabe o que
     * mora na faixa; o cabeçalho garante a altura, o padding e o filete.
     *
     * Exclusivo com `tabs`/`steps` — a faixa é uma só.
     */
    band?: ReactNode;
    /**
     * Telas de coluna estreita (configurações): o título entra na MESMA
     * coluna do conteúdo e a divisória de ponta a ponta some — senão o
     * filete atravessa a tela inteira e o título fica alinhado com a borda
     * enquanto o conteúdo está no meio, que é o que faz a página parecer
     * dois pedaços soltos.
     *
     * A largura da coluna vem do token `--page-col`, declarado na casca da
     * tela. É ele que garante que a borda esquerda do título caia exatamente
     * sobre a do conteúdo.
     */
    centered?: boolean;
}

/**
 * A segunda faixa (abas ou passos) no celular.
 *
 * Ela já rolava de lado, mas em silêncio: a barra é escondida e nada dizia que
 * havia mais aba depois da borda (em Metas, "Ligações" aparecia cortada; em
 * Tarefas a quarta aba sumia). Aqui a ponta que ainda tem conteúdo esmaece e
 * ganha uma seta que rola a faixa, e a aba ativa entra à vista sozinha. A faixa
 * em si (`children`) continua a mesma; no computador este invólucro nem existe.
 */
const TrilhoDoCelular: FC<{ ativo?: string; rotulo: string; children: ReactNode }> = ({ ativo, rotulo, children }) => {
    const caixa = useRef<HTMLDivElement>(null);
    const [lados, setLados] = useState({ esq: false, dir: false });
    const faixa = () => (caixa.current?.firstElementChild as HTMLElement | null) ?? null;

    const medir = () => {
        const el = faixa();
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        const esq = el.scrollLeft > 4;
        const dir = el.scrollLeft < max - 4;
        setLados(l => (l.esq === esq && l.dir === dir ? l : { esq, dir }));
    };

    useEffect(() => {
        const el = faixa();
        if (!el) return;
        el.addEventListener('scroll', medir, { passive: true });
        const observador = new ResizeObserver(medir);
        observador.observe(el);
        return () => { el.removeEventListener('scroll', medir); observador.disconnect(); };
    }, []);

    /* A contagem das abas chega depois e alarga a faixa sem mudar o tamanho
       dela: mede de novo a cada desenho (o `setLados` ignora se nada mudou). */
    useEffect(medir);

    /* A aba escolhida não pode ficar atrás da borda: quem abre Tarefas já na
       quarta aba precisa ver qual está marcada. */
    useEffect(() => {
        const el = faixa();
        const alvo = el?.querySelector<HTMLElement>('.is-active');
        if (!el || !alvo) return;
        const r = alvo.getBoundingClientRect();
        const c = el.getBoundingClientRect();
        if (r.right > c.right) el.scrollLeft += r.right - c.right + 40;
        else if (r.left < c.left) el.scrollLeft -= c.left - r.left + 40;
    }, [ativo]);

    const rolar = (sentido: 1 | -1) => {
        const el = faixa();
        el?.scrollBy({ left: sentido * el.clientWidth * 0.7, behavior: 'smooth' });
    };

    return (
        <div ref={caixa} className={`ph-trilho ${lados.esq ? 'tem-esq' : ''} ${lados.dir ? 'tem-dir' : ''}`}>
            {children}
            {lados.esq && (
                <button type="button" className="ph-trilho-seta is-esq" onClick={() => rolar(-1)}
                    aria-label={`Ver ${rotulo} anteriores`}>
                    <span><LuChevronLeft size={15} /></span>
                </button>
            )}
            {lados.dir && (
                <button type="button" className="ph-trilho-seta is-dir" onClick={() => rolar(1)}
                    aria-label={`Ver mais ${rotulo}`}>
                    <span><LuChevronRight size={15} /></span>
                </button>
            )}
        </div>
    );
};

/**
 * Cabeçalho padrão de tela: faixa do título (com ações à direita) e, abaixo,
 * a faixa de abas — cada uma fechada por uma divisória de ponta a ponta.
 * Sangra até as bordas do conteúdo, então precisa ficar FORA do padding da
 * página: use `.page-shell` + `.page-body` (ver docs/designsystem).
 *
 * `centered` troca isso pela variante de coluna estreita — ver a prop.
 */
const PageHeader: FC<PageHeaderProps> = ({
    leading,
    title,
    titleInfo,
    subtitle,
    actions,
    tabs,
    activeTab,
    onTabChange,
    steps,
    activeStep,
    onStepChange,
    band,
    centered = false,
}) => {
    /* A porta do agente é a última ação de TODA tela (direção B, 06/10) — por
       isso mora aqui e não em cada página. Ela some sozinha onde não cabe
       (Home, tela cheia do assistente, fora do MainLayout). */
    const agente = useAgente();
    const { pathname } = useLocation();
    /* No celular as ações descem para baixo do título; a porta sozinha ali
       seria uma linha com um botão só. Ela vai para o cabeçalho fixo do
       MobileNav — uma porta por largura, nunca duas. */
    const celular = useCelular();
    const temPorta = !!agente && !celular && !rotaSemPortaDoAgente(pathname);

    /* No computador a faixa vai direto, como sempre foi. */
    const noTrilho = (ativo: string | undefined, rotulo: string, faixa: ReactNode) => (
        celular ? <TrilhoDoCelular ativo={ativo} rotulo={rotulo}>{faixa}</TrilhoDoCelular> : faixa
    );

    return (
    <header className={`ph ${centered ? 'is-centered' : ''}`}>
        <div className="ph-title-row">
            {leading}
            <div className="ph-title-block">
                <h1 className="ph-title">{title}{titleInfo}</h1>
                {subtitle && <p className="ph-subtitle">{subtitle}</p>}
            </div>
            {(actions || temPorta) && (
                <div className="ph-actions">
                    {/* 07/10, em teste a pedido dele: a porta ANTES das ações
                        da tela, não depois. */}
                    {temPorta && <AgentePorta />}
                    {actions}
                </div>
            )}
        </div>

        {band && <div className="ph-band">{band}</div>}

        {tabs && tabs.length > 0 && noTrilho(activeTab, 'abas',
            <nav className="ph-tabs">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        type="button"
                        className={`ph-tab ${activeTab === tab.id ? 'is-active' : ''}`}
                        onClick={() => onTabChange?.(tab.id)}
                    >
                        {tab.icon}
                        <span>{tab.label}</span>
                        {tab.count !== undefined && (
                            <span className="ph-tab-count">{tab.count}</span>
                        )}
                    </button>
                ))}
            </nav>
        )}

        {steps && steps.length > 0 && noTrilho(activeStep, 'passos',
            <nav className="ph-steps" aria-label="Passos">
                {steps.map((step, i) => {
                    const atual = activeStep === step.id;
                    const alcancavel = step.done || atual;
                    return (
                        <Fragment key={step.id}>
                            {i > 0 && (
                                <span
                                    className={`ph-step-sep ${steps[i - 1].done ? 'is-done' : ''}`}
                                    aria-hidden="true"
                                />
                            )}
                            <button
                                type="button"
                                className={`ph-step ${atual ? 'is-active' : ''} ${step.done ? 'is-done' : ''}`}
                                aria-current={atual ? 'step' : undefined}
                                disabled={!alcancavel}
                                onClick={() => alcancavel && !atual && onStepChange?.(step.id)}
                            >
                                <span className="ph-step-n" aria-hidden="true">
                                    {step.done && !atual ? <LuCheck size={11} /> : i + 1}
                                </span>
                                <span>{step.label}</span>
                                {step.meta && <span className="ph-step-meta">{step.meta}</span>}
                            </button>
                        </Fragment>
                    );
                })}
            </nav>
        )}
    </header>
    );
};

export default PageHeader;
